import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import {
  EmptyState,
  ParentPageHeader,
  PortalLoading,
  StatusBadge,
  money,
  niceDate,
  niceDateTime,
  useParentPortal,
} from "./parentPortalUtils";

const emptyPaymentForm = {
  invoice: "",
  amount: "",
  mobile_number: "",
  method: "ecocash",
};

function ParentChildFinancePage() {
  const { studentId } = useParams();
  const { data, loading, reload } = useParentPortal(`parent-portal/children/${studentId}/finance/`);
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);
  const [proofForm, setProofForm] = useState({ paymentId: "", proof_note: "" });
  const [proofFile, setProofFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const fee = data?.fee_status || {};
  const invoices = fee.invoices || [];
  const paynowPayments = data?.paynow_payments || [];

  const selectedInvoice = useMemo(
    () => invoices.find((invoice) => String(invoice.id) === String(paymentForm.invoice)),
    [invoices, paymentForm.invoice]
  );

  if (loading && !data) return <PortalLoading />;

  const updatePaymentForm = (field, value) => {
    setPaymentForm((previous) => {
      const next = { ...previous, [field]: value };
      if (field === "invoice") {
        const invoice = invoices.find((item) => String(item.id) === String(value));
        next.amount = invoice?.balance || "";
      }
      return next;
    });
  };

  const initiatePaynow = async (event) => {
    event.preventDefault();
    if (!paymentForm.invoice || !paymentForm.amount || !paymentForm.mobile_number) {
      toast.warning("Choose invoice, amount and mobile number first.");
      return;
    }

    try {
      setSaving(true);
      const response = await axiosInstance.post(
        `parent-portal/children/${studentId}/finance/paynow/initiate/`,
        paymentForm
      );
      toast.success(response.data?.message || "Paynow payment initiated.");
      setPaymentForm(emptyPaymentForm);
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate Paynow payment.");
    } finally {
      setSaving(false);
    }
  };

  const uploadProof = async (event) => {
    event.preventDefault();
    if (!proofForm.paymentId) {
      toast.warning("Select a Paynow payment first.");
      return;
    }
    if (!proofFile && !proofForm.proof_note.trim()) {
      toast.warning("Attach proof or enter a proof note/reference.");
      return;
    }

    try {
      setSaving(true);
      const payload = new FormData();
      if (proofFile) payload.append("proof_file", proofFile);
      payload.append("proof_note", proofForm.proof_note || "");
      const response = await axiosInstance.patch(
        `parent-portal/paynow-payments/${proofForm.paymentId}/proof/`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(response.data?.message || "Proof submitted.");
      setProofForm({ paymentId: "", proof_note: "" });
      setProofFile(null);
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit proof.");
    } finally {
      setSaving(false);
    }
  };

  const checkStatus = async (paymentId) => {
    try {
      setSaving(true);
      await axiosInstance.post(`parent-portal/paynow-payments/${paymentId}/check-status/`);
      toast.success("Paynow status checked.");
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not check Paynow status.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <ParentPageHeader
        title="Child Fees"
        subtitle={data?.student?.full_name || "Fee status, invoices and Paynow payments"}
        icon="bi-cash-stack"
        actions={<button className="btn btn-light border" onClick={reload}>Refresh</button>}
      />

      <div className="row g-3 mb-3">
        <div className="col-md-4"><Summary label="Status" value={<StatusBadge value={fee.status_label} />} /></div>
        <div className="col-md-4"><Summary label="Amount Paid" value={money(fee.amount_paid)} /></div>
        <div className="col-md-4"><Summary label="Outstanding" value={money(fee.outstanding_balance)} /></div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-xl-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="fw-bold mb-1">Pay Fees via Paynow</h5>
              <p className="text-muted small mb-3">
                Start the payment from here, complete the prompt on your phone, then submit proof for school verification.
              </p>

              <form className="row g-3" onSubmit={initiatePaynow}>
                <div className="col-12">
                  <label className="form-label fw-semibold">Invoice</label>
                  <select
                    className="form-select"
                    value={paymentForm.invoice}
                    onChange={(event) => updatePaymentForm("invoice", event.target.value)}
                    required
                  >
                    <option value="">Select invoice</option>
                    {invoices.filter((invoice) => Number(invoice.balance) > 0).map((invoice) => (
                      <option key={invoice.id} value={invoice.id}>
                        {invoice.invoice_number} - {invoice.currency || invoice.currency_code} {money(invoice.balance)} balance
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={selectedInvoice?.balance || undefined}
                    className="form-control"
                    value={paymentForm.amount}
                    onChange={(event) => updatePaymentForm("amount", event.target.value)}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Mobile Number</label>
                  <input
                    className="form-control"
                    placeholder="e.g. 0772123456"
                    value={paymentForm.mobile_number}
                    onChange={(event) => updatePaymentForm("mobile_number", event.target.value)}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Payment Method</label>
                  <select
                    className="form-select"
                    value={paymentForm.method}
                    onChange={(event) => updatePaymentForm("method", event.target.value)}
                  >
                    <option value="ecocash">EcoCash</option>
                    <option value="onemoney">OneMoney</option>
                    <option value="telecash">Telecash</option>
                  </select>
                </div>
                <div className="col-12">
                  <button className="btn btn-success" disabled={saving}>
                    <i className="bi bi-phone me-2"></i>
                    {saving ? "Processing..." : "Start Paynow Payment"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-xl-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="fw-bold mb-1">Submit Payment Proof</h5>
              <p className="text-muted small mb-3">
                Upload screenshot/receipt or enter reference after completing payment. The finance office will verify it.
              </p>

              <form className="row g-3" onSubmit={uploadProof}>
                <div className="col-12">
                  <label className="form-label fw-semibold">Paynow Payment</label>
                  <select
                    className="form-select"
                    value={proofForm.paymentId}
                    onChange={(event) => setProofForm((previous) => ({ ...previous, paymentId: event.target.value }))}
                    required
                  >
                    <option value="">Select payment</option>
                    {paynowPayments.filter((payment) => !["VERIFIED", "REJECTED"].includes(payment.status)).map((payment) => (
                      <option key={payment.id} value={payment.id}>
                        {payment.reference} - {payment.currency_code} {money(payment.amount)} - {payment.status}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Proof File</label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*,.pdf"
                    onChange={(event) => setProofFile(event.target.files?.[0] || null)}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Proof Note / Reference</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    placeholder="Enter EcoCash/OneMoney reference or notes"
                    value={proofForm.proof_note}
                    onChange={(event) => setProofForm((previous) => ({ ...previous, proof_note: event.target.value }))}
                  ></textarea>
                </div>
                <div className="col-12">
                  <button className="btn btn-success" disabled={saving}>
                    <i className="bi bi-upload me-2"></i>
                    Submit Proof
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <h5 className="fw-bold mb-3">Recent Invoices</h5>
          {invoices.length === 0 ? (
            <EmptyState title="No invoices" />
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Invoice</th><th>Term</th><th>Due</th><th>Total</th><th>Paid</th><th>Balance</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id}>
                      <td>{inv.invoice_number}</td>
                      <td>{inv.term || inv.academic_year || "-"}</td>
                      <td>{niceDate(inv.due_date)}</td>
                      <td>{inv.currency || inv.currency_code} {money(inv.total_amount)}</td>
                      <td>{money(inv.amount_paid)}</td>
                      <td>{money(inv.balance)}</td>
                      <td><StatusBadge value={inv.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <h5 className="fw-bold mb-3">Paynow Payment Requests</h5>
          {paynowPayments.length === 0 ? (
            <EmptyState title="No Paynow payments yet" message="Started Paynow payments will appear here." />
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Reference</th><th>Invoice</th><th>Amount</th><th>Status</th><th>Proof</th><th>Created</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paynowPayments.map((payment) => (
                    <tr key={payment.id}>
                      <td><div className="fw-semibold">{payment.reference}</div><small className="text-muted">{payment.method}</small></td>
                      <td>{payment.invoice_number}</td>
                      <td>{payment.currency_code} {money(payment.amount)}</td>
                      <td><StatusBadge value={payment.status} /></td>
                      <td>{payment.proof_url ? <a href={payment.proof_url} target="_blank" rel="noreferrer">View proof</a> : "-"}</td>
                      <td>{niceDateTime(payment.created_at)}</td>
                      <td>
                        <button className="btn btn-sm btn-light border" onClick={() => checkStatus(payment.id)} disabled={saving || !payment.poll_url}>
                          Check Status
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <small className="text-muted fw-bold">{label}</small>
        <div className="fs-4 fw-bold mt-1">{value}</div>
      </div>
    </div>
  );
}

export default ParentChildFinancePage;
